import React, { useCallback, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, H1, IconLink, Text, VStack } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { NotFound } from '@betterment/shared-components/src/components/not-found/NotFound';
import { useNavigate, useParams } from 'react-router-dom';

import { EditSecuritiesLendingCard } from './EditSecuritiesLendingCard';
import { HouseholdPortfolioControlsDocument } from './graphql/query.householdPortfolioControls.generated';
import { SecuritiesLendingInviteDialog } from './SecuritiesLendingInviteDialog';

import { IdempotencyProviderWithFeatureRegistry } from '../../../providers/IdempotencyProviderWithFeatureRegistry';
import { WorkflowPage } from '../../../Shared/Components/WorkflowPage';
import urlBuilder from '../../../Shared/url-builder';

export const EditSecuritiesLending: React.FC = () => {
  const navigate = useNavigate();
  const { householdId } = useParams<{ householdId: string }>();
  const [inviteMemberId, setInviteMemberId] = useState<string | null>(null);
  const [inviteSuccessMemberId, setInviteSuccessMemberId] = useState<string | null>(null);
  const handleOpenInviteModal = useCallback((memberId: string) => () => setInviteMemberId(memberId), []);
  const handleCloseInviteModal = useCallback(() => setInviteMemberId(null), []);
  const handleInviteSuccess = useCallback(() => {
    setInviteSuccessMemberId(inviteMemberId);
    setInviteMemberId(null);
  }, [inviteMemberId]);

  const { data } = useGraphQLQuery(HouseholdPortfolioControlsDocument, {
    variables: { id: householdId! },
  });

  if (!data) {
    return <CardLoader />;
  }

  if (data.household.__typename === 'RecordNotFoundResult') {
    return <NotFound backPath={urlBuilder.getHouseholdsListPage.absolute()} backName="the clients page" />;
  }

  const { members } = data.household;

  if (members.length === 0) {
    return null;
  }

  const handleCancel = () => {
    void navigate(urlBuilder.getHouseholdsSettingsPage.absolute({ householdId: householdId! }));
  };

  return (
    <WorkflowPage>
      <WorkflowPage.Header>
        <Box />
        <Box css={{ display: 'flex', justifyContent: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <Text variant="text50">Manage securities lending</Text>
        </Box>
        <Box css={{ justifySelf: 'flex-end' }}>
          <IconLink
            label="Cancel"
            name="X"
            spacing="compact"
            variant="ghostSecondary"
            role="button"
            onClick={handleCancel}
          />
        </Box>
      </WorkflowPage.Header>

      <WorkflowPage.Main>
        <Box css={{ maxWidth: '600px', margin: '0 auto' }}>
          <VStack css={{ gap: 'space.400' }}>
            <VStack css={{ gap: 'space.200' }}>
              <H1 variant="title400">Manage securities lending.</H1>
              <Text variant="text100">
                Securities lending is coming soon. Clients will receive an invite email to confirm their enrollment.
              </Text>
              <Text variant="text100">
                For joint accounts, the primary account owner's lending status applies. You can update these settings at
                any time.
              </Text>
            </VStack>

            <VStack css={{ gap: 'space.300' }}>
              {members.map(member => (
                <EditSecuritiesLendingCard
                  key={member.id}
                  member={member}
                  handleOpenInviteModal={handleOpenInviteModal}
                  showInviteSentBanner={inviteSuccessMemberId === member.id}
                />
              ))}
            </VStack>

            <Text variant="text50" css={{ color: 'text.weak' }}>
              By electing to present the securities lending opt-in option to your client, you acknowledge and agree that
              you have diligently reviewed and confirmed, based on your client's financial profile, that securities
              lending is suitable and appropriate for your client's account. Clients will be automatically enrolled once
              securities lending becomes available. Apex is the sole borrower of securities under the Fully-Paid
              Securities Lending (“FPSL”) Program and may lend out clients' securities. Loaned securities are not
              covered by SIPC, but are backed by at least 100% cash collateral held at a third-party bank.
            </Text>
          </VStack>
        </Box>
      </WorkflowPage.Main>

      <IdempotencyProviderWithFeatureRegistry featureName="secLending">
        <SecuritiesLendingInviteDialog
          inviteMemberId={inviteMemberId}
          memberName={members?.find(m => m.id === inviteMemberId)?.commonFirstName}
          onClose={handleCloseInviteModal}
          onSuccess={handleInviteSuccess}
        />
      </IdempotencyProviderWithFeatureRegistry>
    </WorkflowPage>
  );
};
